import {observer} from 'mobx-react-lite';
import moment from 'moment';
import React, {useCallback, useContext, useMemo} from 'react';
import {useTranslation} from 'react-i18next';
import {EIconButton} from 'webapp-lib/Components';
import {ContextAction, useContextMenu} from 'webapp-lib/Functions';

import {List, ListItem, ListItemProps, Loading} from '#';
import {
  ActivitySharedNotification,
  DocumentSharedNotification,
  InaccessibleActivityCreatedNotification,
  InvoiceContainerCompleteByDueDateReachedNotification,
  Notification,
  ObligationVersionReviewNeededNotification,
  SetAsResponsiblePersonOfTheCasusNotification,
  TaskAssignedNotification,
  TaskOwnerChangedNotification,
} from '@lexian/api/notifications-api';
import {
  CasusIcon,
  ChevronLargeRight,
  Clock,
  FinanceIcon,
  GenericDocument,
  Obligation,
  Task,
} from '_/assets/images';
import {I18NextNS} from '_/i18n';
import {State} from '_/utilities/types';

import {StoreContext} from '../../store';

import {
  DateWrapper,
  ExclamationMark,
  RowContainer,
  StyledNotificationsList,
} from './styles';

type contextTypes =
  | DocumentSharedNotification['context']
  | ObligationVersionReviewNeededNotification['context']
  | SetAsResponsiblePersonOfTheCasusNotification['context']
  | TaskOwnerChangedNotification['context']
  | TaskAssignedNotification['context']
  | ActivitySharedNotification['context']
  | InvoiceContainerCompleteByDueDateReachedNotification['context']
  | InaccessibleActivityCreatedNotification['context'];

const icons: Record<contextTypes, any> = {
  CASUS_MANAGEMENT: <CasusIcon />,
  DOCUMENT_MANAGEMENT: <GenericDocument />,
  INVOICE_MANAGEMENT: <FinanceIcon />,
  OBLIGATION_MANAGEMENT: <Obligation />,
  TASK_MANAGEMENT: <Task />,
  TIMETRACKING: <Clock />,
};

function NotificationItemComponent({
  item,
  onNotificationClick,
  ref,
  ...props
}: ListItemProps<Notification> & {
  onNotificationClick: (arg0: Notification) => void;
}): React.ReactElement {
  const {t} = useTranslation<I18NextNS>('notifications');
  const store = useContext(StoreContext);

  const handleClick = useCallback(() => {
    onNotificationClick(item);
  }, [onNotificationClick, item]);

  const actions: ContextAction<Notification>[] = useMemo(
    () => [
      {
        id: 'mark-important',
        callback: (): void => {
          store.setImportantFlag({
            newFlag: !item.importantFlag,
            notificationId: item.id,
          });
        },
        text: item.importantFlag
          ? t('notificationList.actions.markAsUnimportant')
          : t('notificationList.actions.markAsImportant'),
      },
      {
        id: 'mark-read',
        callback: (): void => {
          store.setReadFlag({
            newFlag: !item.readFlag,
            notificationId: item.id,
          });
        },
        text: item.readFlag
          ? t('notificationList.actions.markAsUnread')
          : t('notificationList.actions.markAsRead'),
      },
      {
        id: 'delete',
        callback: (): void => {
          store.deleteNotification({notificationId: item.id});
        },
        text: t('common:actions.delete'),
      },
      {
        id: 'reminders-in',
        subActions: [
          {
            id: 'five-mins',
            callback: (): void => {
              store.setDelay({
                newDate: moment().add('5', 'minutes').utc().format(),
                notificationId: item.id,
              });
            },
            text: t('notificationList.actions.fiveMin'),
          },
          {
            id: 'thirty-mins',
            callback: (): void => {
              store.setDelay({
                newDate: moment().add('30', 'minutes').utc().format(),
                notificationId: item.id,
              });
            },
            text: t('notificationList.actions.thirtyMin'),
          },
          {
            id: 'one-hour',
            callback: (): void => {
              store.setDelay({
                newDate: moment().add('1', 'hour').utc().format(),
                notificationId: item.id,
              });
            },
            text: t('notificationList.actions.oneHour'),
          },
        ],
        text: t('notificationList.actions.remindersIn'),
      },
    ],
    [item.importantFlag, item.readFlag, item.id, t, store]
  );

  const {contextMenuCallbacks, contextMenu} = useContextMenu(actions, item);

  return (
    <ListItem
      ref={ref}
      item={item}
      onClick={handleClick}
      {...contextMenuCallbacks}
      {...props}
    >
      {contextMenu}
      {icons[item.context as contextTypes]}
      <ExclamationMark data-testid='exclamation-mark'>
        {item.importantFlag ? '!' : ''}
      </ExclamationMark>
      <RowContainer isUnread={!item.readFlag}>{item.message}</RowContainer>
      <DateWrapper isUnread={!item.readFlag}>
        {moment(item.notifyAt).diff(moment().startOf('day'), 'days') === 0
          ? moment(item.notifyAt).format('HH:mm')
          : t('common:dateFormat.withoutTime', {
              date: new Date(item.notifyAt),
            })}
      </DateWrapper>
      <EIconButton onClick={handleClick}>
        <ChevronLargeRight />
      </EIconButton>
    </ListItem>
  );
}

const ItemComponent = observer(NotificationItemComponent);

interface Props {
  className?: string;
  onChange: (notification: Notification) => void;
}

function NotificationsListComponent({
  className,
  onChange,
}: Props): React.ReactElement {
  const store = useContext(StoreContext);
  const staticProps = useMemo(
    () => ({onNotificationClick: onChange}),
    [onChange]
  );
  const {t} = useTranslation<I18NextNS>('notifications');

  return (
    <StyledNotificationsList className={className}>
      {store.notifications.state === State.Loading ? (
        <Loading />
      ) : (
        <List
          ItemComponent={ItemComponent}
          items={store.shownNotifications}
          placeholderHeader={
            store.settings.visibleNotifications === 'INFORMATIVE'
              ? t('notificationsNotFound.informative')
              : store.settings.visibleNotifications === 'RESOLVABLE'
                ? t('notificationsNotFound.resolvable')
                : t('notificationsNotFound.all')
          }
          staticProps={staticProps}
        />
      )}
    </StyledNotificationsList>
  );
}

export default observer(NotificationsListComponent);
